import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OpportunityDetailsCard, CommerceHubPanel, ProductsFinanceHubProvider } from '..';
import { useDashboardStoreBindings } from '../hooks/useDashboardStoreBindings';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import type { DashboardSplitMainPanelsProps } from './DashboardSplitMainPanels';
import { DashboardV3ProjectHeader } from './DashboardV3ProjectHeader';
import { useViewerStore } from '../../../../../store/store';
import { DashboardV3MilestonesRow } from './DashboardV3MilestonesRow';
import { DashboardV3SelectedProductsSection } from './dashboardV3/DashboardV3SelectedProductsSection';
import { DashboardV3PricingFinanceSection } from './dashboardV3/DashboardV3PricingFinanceSection';
import { DashboardV3PartnerSyncSection } from './dashboardV3/DashboardV3PartnerSyncSection';
import { DashboardV3SectionModals } from './dashboardV3/DashboardV3SectionModals';
import '../oneButton-css/DashboardV3LayoutSection.css';

/**
 * Proposal-style dashboard (V3): mockup-aligned section cards; detail flows open in modals.
 */
export const DashboardV3MainPanels: React.FC<DashboardSplitMainPanelsProps> = ({
    theme,
    siteAddress,
    titleSearchData,
    oppRefreshKey,
    setAddTitleContactsModalOpen,
    handleOpenBlackKnightLeadModal,
    triggerPricingGridForDesign,
    priceQuote,
    isDashboardOpen,
    pricingFetchTrigger,
    pricingDesignForTrigger,
    setPricingFetchTrigger,
    opportunityDetails,
    selectedDesignForPricing,
    setPricingGridModalOpen,
    opportunityID,
    designsDashboardSlot,
}) => {
    const { loadingCompleted, refreshingShadeFromStore, designListFromStore, siteDesignDetailsFromStore } =
        useDashboardStoreBindings({});

    const setCommerceHubQuotesOpen = useOneButtonStore((s) => s.setCommerceHubQuotesOpen);
    const setFinancePartnerSyncModalOpen = useOneButtonStore((s) => s.setFinancePartnerSyncModalOpen);
    const contactsOpen = useOneButtonStore((s) => s.dashboardContactsModalOpen);
    const setDashboardContactsModalOpen = useOneButtonStore((s) => s.setDashboardContactsModalOpen);

    const [aiDesignsOpen, setAiDesignsOpen] = useState(false);

    const opportunityNumericId =
        opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? null;
    const dashboardOpportunityId = sessionStorage.getItem('SalesForceId') || '';
    const dashboardOpportunityName = sessionStorage.getItem('opportunityName') || '';
    const dashboardStageNumber = sessionStorage.getItem('currentStage') || '';
    const dashboardStageStatus = sessionStorage.getItem('stageStatus') || '';
    const dashboardUtilityCompany = sessionStorage.getItem('Utility_Company') || '';

    const designAndShadeReady = Boolean(loadingCompleted && !refreshingShadeFromStore);
    const hasPrimaryDesign = Array.isArray(designListFromStore) && designListFromStore.length > 0;
    const hasSiteDesignDetails = Array.isArray(siteDesignDetailsFromStore) && siteDesignDetailsFromStore.length > 0;
    const isDashboardDataLoading = !designAndShadeReady && !hasPrimaryDesign && !hasSiteDesignDetails;

    const salesForceId =
        opportunityDetails?.SalesForce_Id ??
        (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) ??
        undefined;

    const onSolarPricingClick = useCallback(() => {
        if (selectedDesignForPricing) {
            triggerPricingGridForDesign(selectedDesignForPricing);
            return;
        }
        setPricingGridModalOpen(true);
        setPricingFetchTrigger(Date.now());
    }, [
        selectedDesignForPricing,
        setPricingFetchTrigger,
        setPricingGridModalOpen,
        triggerPricingGridForDesign,
    ]);

    const designsMenubarSignal = useViewerStore((s) => s.designsMenubarSignal);
    const prevMenubarSignalRef = useRef(designsMenubarSignal);
    useEffect(() => {
        if (designsMenubarSignal > prevMenubarSignalRef.current) {
            setAiDesignsOpen(false);
        }
        prevMenubarSignalRef.current = designsMenubarSignal;
    }, [designsMenubarSignal]);

    const openDealHealth = useCallback(() => {
        setPricingGridModalOpen(true);
        setPricingFetchTrigger(Date.now());
    }, [setPricingFetchTrigger, setPricingGridModalOpen]);

    return (
        <ProductsFinanceHubProvider salesForceId={salesForceId}>
        <div className={`obd-dashboard-v3-layout obd-theme-${theme}`}>
            <div className="obd-dashboard-v3-layout__preload" aria-hidden>
                <OpportunityDetailsCard
                    loading={isDashboardDataLoading}
                    opportunityId={dashboardOpportunityId}
                    opportunityName={dashboardOpportunityName}
                    stageStatus={dashboardStageStatus}
                    stageNumber={dashboardStageNumber}
                    utilityCompany={dashboardUtilityCompany}
                    siteAddress={siteAddress}
                    refreshKey={oppRefreshKey}
                />
            </div>

            <DashboardV3ProjectHeader
                theme={theme}
                opportunityName={dashboardOpportunityName}
                opportunityId={dashboardOpportunityId}
                stageStatus={dashboardStageStatus}
                siteAddress={siteAddress}
                utilityCompany={dashboardUtilityCompany}
                loading={isDashboardDataLoading}
                isTitleContactExist={titleSearchData === null ? null : titleSearchData.length > 0}
                onAddTitleContactsClick={() => setAddTitleContactsModalOpen(true)}
                onBlackKnightTitleCheckClick={handleOpenBlackKnightLeadModal}
            />

            <DashboardV3SelectedProductsSection onOpenProducts={() => undefined} />

            <div className="obd-dashboard-v3-layout__pricing-row">
                <DashboardV3PricingFinanceSection
                    onOpenDealHealth={openDealHealth}
                    onOpenPricingOptions={openDealHealth}
                    onEditLine={() => setCommerceHubQuotesOpen(true)}
                />
                <DashboardV3PartnerSyncSection
                    onOpenSyncStatus={() => setFinancePartnerSyncModalOpen(true)}
                />
            </div>

            <section className="obd-dashboard-v3-layout__milestones" aria-label="Status and milestones">
                <h2 className="obd-dashboard-v3-layout__section-title">Status &amp; Milestones</h2>
                <DashboardV3MilestonesRow
                    theme={theme}
                    opportunityNumericId={opportunityNumericId}
                    salesForceId={salesForceId}
                />
            </section>

            <DashboardV3SectionModals
                theme={theme}
                dashboardUiVariant="v3"
                contactsOpen={contactsOpen}
                onCloseContacts={() => setDashboardContactsModalOpen(false)}
                aiDesignsOpen={aiDesignsOpen}
                onCloseAiDesigns={() => setAiDesignsOpen(false)}
                designsDashboardSlot={designsDashboardSlot}
                contactsProps={{
                    loading: isDashboardDataLoading,
                    opportunityId: dashboardOpportunityId,
                    isTitleContactExist: titleSearchData === null ? null : titleSearchData.length > 0,
                    onAddTitleContactsClick: () => setAddTitleContactsModalOpen(true),
                    onBlackKnightTitleCheckClick: handleOpenBlackKnightLeadModal,
                }}
            />

            <div className="obd-dashboard-v3-layout__commerce-host" aria-hidden>
                <CommerceHubPanel
                    salesForceId={salesForceId}
                    loading={isDashboardDataLoading}
                    onSolarPricingClick={onSolarPricingClick}
                    opportunityNumericId={opportunityNumericId}
                    priceQuote={priceQuote}
                />
            </div>
        </div>
        </ProductsFinanceHubProvider>
    );
};
